// Copyright (C) Microsoft Corporation. All rights reserved.

import type { Prompt, PromptTier } from "@1js/pl-card-components";
import { type DataField, type TelemetryLogger } from "@microsoft/oteljs";
import {
  HandoffErrors,
  type HandoffErrorsType,
} from "../../hooks/handoff.types";
import {
  ActionInputMethods,
  ActionTarget,
  FeatureActionType,
  getDataFieldName,
} from "./config";
import { dictionaryToDataFields, getPartBDataFields } from "./dataField";
import type { Events } from "./events";
import { getEventName } from "./events";
import getOrCreateTelemetryLogger from "./getTelemetryLogger";
import { getEcsConfigIds, getPageLoadTime, getPromptOrigin } from "./utils";

declare global {
  interface Window {
    // Note: the following line is used to set the OCV Feedback contextual data (e.g last clicked prompt)
    setFeedbackContextData: (prompt: Prompt) => void;
  }
}

/**
 * Send Telemetry Event
 * @param eventName Event Name
 * @param dataFields Optional Otel Data Fields
 * @param isIntentional Indicates weather this action is an intentional action, e.g., PageInit that is automated
 * is not an intentional action.
 * @param actionInputMethod NGP Export attributes. Examples are mouse, keyboard, track pad, controller, touch, etc.
 * @param actionTarget Can pertain to, for example a document target destination path or a UX element that the action
 * @param featureActionType What's type of action represent for the feature?
 */
export const sendTelemetryEvent = (
  eventName: keyof typeof Events,
  dataFields?: {
    [key: string]: string | boolean | number | undefined;
  },
  isIntentional?: boolean,
  actionInputMethod?: ActionInputMethods,
  actionTarget?: ActionTarget,
  featureActionType?: FeatureActionType
) => {
  const telemetryLogger: TelemetryLogger = getOrCreateTelemetryLogger();

  dataFields = dataFields || {};

  // Add ECS flight details
  dataFields["EcsConfigIds"] = getEcsConfigIds();

  const eventDataFields: DataField[] = [
    ...dictionaryToDataFields(dataFields),
    ...getPartBDataFields(
      eventName,
      isIntentional,
      actionInputMethod,
      actionTarget,
      featureActionType
    ),
  ];

  telemetryLogger.sendTelemetryEvent({
    eventName: getEventName(eventName),
    dataFields: eventDataFields,
    eventFlags: {
      // TODO: We should use the the following const enums from the @microsoft/oteljs package instead of manually setting the values
      // There is a TypeScript error (TS2748) that prevents us from doing so
      //
      //samplingPolicy: SamplingPolicy.NotSet,
      //costPriority: CostPriority.NotSet,
      //persistencePriority: PersistencePriority.NotSet,
      //diagnosticLevel: DiagnosticLevel.RequiredServiceData,
      //dataCategories: DataCategories.ProductServiceUsage,
      samplingPolicy: 0,
      costPriority: 0,
      persistencePriority: 0,
      diagnosticLevel: 110,
      dataCategories: 2,
    },
  });
};

/**
 * Send an intentional Click event
 *
 * @param eventName Event Name
 * @param dataFields Optional Otel Data Fields
 * @param actionTarget Can pertain to, for example a document target destination path or a UX element that the action
 * @param featureActionType What's type of action represent for the feature?
 */
function sendClickTelemetryEvent(
  eventName: keyof typeof Events,
  dataFields?: {
    [key: string]: string | number | boolean | undefined;
  },
  actionTarget = ActionTarget.Button,
  featureActionType = FeatureActionType.IsTried
) {
  sendTelemetryEvent(
    eventName,
    dataFields,
    true,
    ActionInputMethods.All,
    actionTarget,
    featureActionType
  );
}

/**
 * Sends a Page Init telemetry event (before the window "load" event)
 */
export function sendPageInitEvent() {
  sendTelemetryEvent("PageInit");
}

/**
 * Sends a Page Load telemetry event (after the window "load" event)
 */
export function sendPageLoadEvent() {
  sendTelemetryEvent("PageLoad", {
    "Page.LoadTime": getPageLoadTime(),
  });
}

/**
 * Sends a Page Shutdown telemetry event (after the window "beforeunload" event)
 */
export function sendPageShutdownEvent() {
  sendTelemetryEvent("Shutdown");
}

/**
 * Sends a Prompt Clicked telemetry event
 * @param prompt Prompt Object
 * @param isFeatured Is featured prompt?
 */
export function sendPromptClickedEvent(prompt: Prompt, isFeatured = false) {
  // Note: the following line is used to set the OCV Feedback contextual data (e.g last clicked prompt)
  // This is unfortunately creating unnecessary coupling with the the Telemetry module and needs to be improved
  if (typeof window.setFeedbackContextData === "function") {
    window.setFeedbackContextData(prompt);
  }

  sendClickTelemetryEvent("PromptClicked", {
    // TODO: We can't log Prompt ID for now since it's being treated as customer content
    //"Prompt.Id": prompt.Id,
    [getDataFieldName("Origin")]: getPromptOrigin(prompt),
    [getDataFieldName("Featured")]: isFeatured,
    [getDataFieldName("ReferenceId")]: prompt.ReferenceId ?? "null",
    [getDataFieldName("LogicalId")]: prompt.LogicalId,
    [getDataFieldName("DisplayCategory")]: prompt.DisplayCategory,
    [getDataFieldName("Products")]: prompt.Products.join(","),
  });
}

/**
 * Sends a Copy Prompt telemetry event
 * @param prompt Prompt Object
 * @param isFeatured Is featured prompt?
 */
export function sendCopyPromptEvent(prompt: Prompt, isFeatured = false) {
  sendClickTelemetryEvent("CopyPrompt", {
    // TODO: We can't log Prompt ID for now since it's being treated as customer content
    //"Prompt.Id": prompt.Id,
    [getDataFieldName("Origin")]: getPromptOrigin(prompt),
    [getDataFieldName("Featured")]: isFeatured,
    [getDataFieldName("DisplayCategory")]: prompt.DisplayCategory,
    [getDataFieldName("Products")]: prompt.Products.join(","),
  });
}

/**
 * Sends a Share Prompt telemetry event
 * @param prompt Prompt Object
 * @param isFeatured Is featured prompt?
 */
export function sendSharePromptEvent(prompt: Prompt, isFeatured = false) {
  sendClickTelemetryEvent("SharePrompt", {
    // TODO: We can't log Prompt ID for now since it's being treated as customer content
    //"Prompt.Id": prompt.Id,
    [getDataFieldName("Origin")]: getPromptOrigin(prompt),
    [getDataFieldName("Featured")]: isFeatured,
    [getDataFieldName("DisplayCategory")]: prompt.DisplayCategory,
    [getDataFieldName("Products")]: prompt.Products.join(","),
  });
}

/**
 * Sends a Share Prompt via Email telemetry event
 * @param prompt Prompt Object
 * @param MsGraphLatency Microsoft Graph call latency
 * @param MsGraphResponseCode Microsoft Graph call response code (e.g. 200, 400)
 */
export function sendSharePromptViaEmailEvent(
  prompt: Prompt,
  MsGraphLatency: number,
  MsGraphResponseCode: number
) {
  sendClickTelemetryEvent("SharePromptViaEmail", {
    // TODO: We can't log Prompt ID for now since it's being treated as customer content
    //"Prompt.Id": prompt.Id,
    [getDataFieldName("Origin")]: getPromptOrigin(prompt),
    [getDataFieldName("ReferenceId")]: prompt.ReferenceId ?? "null",
    [getDataFieldName("DisplayCategory")]: prompt.DisplayCategory,
    [getDataFieldName("Products")]: prompt.Products.join(","),
    "MSGraph.Latency": MsGraphLatency,
    "MSGraph.ResponseCode": MsGraphResponseCode,
  });
}

export function sendTryInAppEventCopy(
  prompt: Prompt,
  product: string,
  error?: HandoffErrorsType
) {
  if (!!error && !HandoffErrors.has(error)) {
    error = "UnknownError";
  }

  sendClickTelemetryEvent("TryInAppCopy", {
    // TODO: We can't log Prompt ID for now since it's being treated as customer content
    //"Prompt.Id": prompt.Id,
    [getDataFieldName("IsUserPrompt")]: prompt.IsUserPrompt,
    [getDataFieldName("DisplayCategory")]: prompt.DisplayCategory,
    [getDataFieldName("Products")]: prompt.Products.join(","),
    [getDataFieldName("ProductTried")]: product,
    [getDataFieldName("Result")]: error,
  });
}

export function sendTryInAppEventError(
  prompt: Prompt,
  product: string,
  error: HandoffErrorsType
) {
  if (!HandoffErrors.has(error)) {
    error = "UnknownError";
  }

  sendClickTelemetryEvent("TryInAppError", {
    // TODO: We can't log Prompt ID for now since it's being treated as customer content
    //"Prompt.Id": prompt.Id,
    [getDataFieldName("IsUserPrompt")]: prompt.IsUserPrompt,
    [getDataFieldName("DisplayCategory")]: prompt.DisplayCategory,
    [getDataFieldName("Products")]: prompt.Products.join(","),
    [getDataFieldName("ProductTried")]: product,
    [getDataFieldName("Result")]: error,
  });
}

export function sendTryInAppEventLoading(prompt: Prompt, product: string) {
  sendClickTelemetryEvent("TryInAppLoading", {
    // TODO: We can't log Prompt ID for now since it's being treated as customer content
    //"Prompt.Id": prompt.Id,
    [getDataFieldName("IsUserPrompt")]: prompt.IsUserPrompt,
    [getDataFieldName("DisplayCategory")]: prompt.DisplayCategory,
    [getDataFieldName("Products")]: prompt.Products.join(","),
    [getDataFieldName("ProductTried")]: product,
  });
}

export function sendTryInAppEventSuccess(prompt: Prompt, product: string) {
  sendClickTelemetryEvent("TryInAppSuccess", {
    // TODO: We can't log Prompt ID for now since it's being treated as customer content
    //"Prompt.Id": prompt.Id,
    [getDataFieldName("IsUserPrompt")]: prompt.IsUserPrompt,
    [getDataFieldName("DisplayCategory")]: prompt.DisplayCategory,
    [getDataFieldName("Products")]: prompt.Products.join(","),
    [getDataFieldName("ProductTried")]: product,
  });
}

export function sendTryInAppEventCancelled(
  prompt: Prompt,
  product: string,
  error?: HandoffErrorsType
) {
  if (!!error && !HandoffErrors.has(error)) {
    error = "UnknownError";
  }
  sendClickTelemetryEvent("TryInAppCancelled", {
    // TODO: We can't log Prompt ID for now since it's being treated as customer content
    //"Prompt.Id": prompt.Id,
    [getDataFieldName("IsUserPrompt")]: prompt.IsUserPrompt,
    [getDataFieldName("DisplayCategory")]: prompt.DisplayCategory,
    [getDataFieldName("Products")]: prompt.Products.join(","),
    [getDataFieldName("ProductTried")]: product,
    [getDataFieldName("Result")]: error,
  });
}

/**
 * Sends a LoadMorePrompts telemetry event
 */
export function sendLoadMorePromptsEvent() {
  sendClickTelemetryEvent("LoadMorePrompts");
}

/**
 * Sends a "More Like This" Prompt Click telemetry event
 * @param prompt Prompt Object
 */
export function sendMoreLikeThisPromptClickEvent(prompt: Prompt) {
  sendClickTelemetryEvent("MoreLikeThisPromptClicked", {
    // TODO: We can't log Prompt ID for now since it's being treated as customer content
    //"Prompt.Id": prompt.Id,
    [getDataFieldName("Origin")]: getPromptOrigin(prompt),
    [getDataFieldName("DisplayCategory")]: prompt.DisplayCategory,
    [getDataFieldName("Products")]: prompt.Products.join(","),
  });
}
/**
 * Sends a PromptsFilter telemetry event
 * @param filters Prompt filter
 */
export function sendPromptsFilterEvent(filters: Record<string, string[]>) {
  sendClickTelemetryEvent(
    "PromptsFilter",
    {
      "Prompt.Filters": JSON.stringify(filters),
    },
    ActionTarget.DropDown
  );
}

/**
 * Sends a PromptBookmarked telemetry event
 * @param prompt Prompt Object
 * @param isFeatured Is featured prompt?
 */
export function sendPromptBookmarkedEvent(prompt: Prompt, isFeatured = false) {
  sendClickTelemetryEvent("PromptBookmarked", {
    // TODO: We can't log Prompt ID for now since it's being treated as customer content
    //"Prompt.Id": prompt.Id,
    [getDataFieldName("Origin")]: getPromptOrigin(prompt),
    [getDataFieldName("Featured")]: isFeatured,
    [getDataFieldName("IsFavorite")]: prompt.UserActivity.Favorite.IsFavorite,
    [getDataFieldName("DisplayCategory")]: prompt.DisplayCategory,
    [getDataFieldName("Products")]: prompt.Products.join(","),
  });
}

/**
 * Sends a SelectTab telemetry event
 * @param promptTier PromptTier
 */
export function sendSelectTabEvent(promptTier: PromptTier) {
  sendClickTelemetryEvent("SelectTab", {
    "Prompt.Tier": promptTier,
  });
}

/**
 * Sends a PageScroll telemetry event
 * @param scrollDetails Page scroll details object
 */
export function sendPageScrollEvent(scrollDetails: {
  viewportWidth: number;
  viewportHeight: number;
  scrollLeft: number;
  scrollTop: number;
  pageHeight: number | undefined;
}) {
  sendTelemetryEvent(
    "PageScroll",
    scrollDetails,
    true,
    ActionInputMethods.Mouse,
    ActionTarget.ScrollArea
  );
}

/**
 * Sends a Click telemetry event
 * @param clickDetails Page click details object
 */
export function sendClickEvent(clickDetails: {
  clickCoordinateX: number;
  clickCoordinateY: number;
  viewportWidth: number;
  viewportHeight: number;
  scrollLeft: number;
  scrollTop: number;
  pageHeight: number | undefined;
}) {
  sendTelemetryEvent("Click", clickDetails, true, ActionInputMethods.Mouse);
}

/**
 * Sends a ResultsRendered event
 * @param E2ELatency e2e latency of prompts rendering process
 * @param is3SServiceEnabled is 3s enabled?
 * @param prompts prompts returned by the allPrompts endpoint
 */
export function sendResultsRenderedEvent(
  E2ELatency: number,
  is3SServiceEnabled: boolean,
  prompts: Prompt[]
) {
  sendTelemetryEvent("ResultsRendered", {
    E2ELatency,
    Is3SServiceEnabled: is3SServiceEnabled,
    [getDataFieldName("LogicalId")]:
      prompts.length > 0 ? prompts[0].LogicalId : undefined,
  });
}

/**
 * Sends a GroupResultsRendered event
 * @param _E2ELatency e2e latency of groups rendering process
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function sendGroupResultsRenderedEvent(_E2ELatency: number) {
  // TODO (anbal): privacy review
  /*sendTelemetryEvent("GroupResultsRendered", {
    E2ELatency,
  });*/
}

/**
 * Sends a ResponseReceived event
 * @param Latency latency of the response
 * @param Status status code of the response
 * @param TraceId trace id of the response
 */
export function sendResponseReceivedEvent(
  Latency: number,
  Status: number,
  TraceId: string
) {
  sendTelemetryEvent("ResponseReceived", {
    Latency,
    Status,
    TraceId,
  });
}

/**
 *
 * See https://docs.substrate.microsoft.net/docs/Substrate-APIs-and-services/Substrate-Search-Service-%283S%29/3S-API-Reference/Events.html?uid=events#client-layout
 * @param prompts
 * @param layoutType
 */
export function sendClientLayoutEvent(
  prompts: Prompt[],
  layoutType: "Vertical" | "Horizontal"
) {
  sendTelemetryEvent("ClientLayout", {
    [getDataFieldName("LogicalId")]:
      prompts.length > 0 ? prompts[0].LogicalId : undefined,
    LayoutType: layoutType,
    ResultsView: JSON.stringify(
      prompts.map((prompt, i) => {
        return {
          Type: "Entity",
          EntityType: "Prompt",
          Name: null,
          ReferenceId: prompt.ReferenceId,
          Position: (i + 1).toString(),
          LayoutType: null,
          ResultsView: null,
        };
      })
    ),
  });
}
